/* eslint-disable react/require-default-props */
/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable react/no-unescaped-entities */
/* eslint-disable react/jsx-curly-brace-presence */
/* eslint-disable no-nested-ternary */

import React, { useState } from 'react';

import {
  Box,
  HStack,
  Collapse,
  VStack,
  Text,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Stack,
} from '@chakra-ui/core';
import isBoolean from 'lodash/isBoolean';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import isArray from 'lodash/isArray';
import { MdArrowRight, MdArrowDropDown } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import theme from '../../../../styles/customTheme';
import { TextWithTranslationsT } from '../types';

type JsonValue = string | number | boolean | null | JsonObject | JsonValue[];
type JsonObject = { [key: string]: JsonValue };
type K = { text?: TextWithTranslationsT; label?: TextWithTranslationsT };
interface JsonViewerProps<T extends K> {
  data: JsonObject;
  comments?: Record<string, T>; // dot notation path to comment
  level?: number;
  path?: string;
}

const getTypeColor = (value: JsonValue): string => {
  switch (typeof value) {
    case 'string':
      return 'orange.500';
    case 'number':
      return 'blue.500';
    case 'boolean':
      return 'teal.500';
    case 'object':
      return value === null ? 'gray.500' : 'orange.500';
    default:
      return 'gray.500';
  }
};

const isCollapsible = (value: JsonValue) => isObject(value) && value !== null;

const JsonNode = <T extends K>({
  k,
  v,
  level,
  path,
  comments,
}: {
  k: string;
  v: JsonValue;
  level: number;
  path: string;
  comments?: Record<string, T>;
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const { i18n, t } = useTranslation('hrFormTemplate');

  const keyPath = path ? `${path}.${k}` : k;
  const indent = level * 16;

  const comment = comments?.[keyPath];
  const keyColor = 'gray.800';
  const emptyBraces = isArray(v) ? '[...]' : '{...}';
  const opeaningBraces = isArray(v) ? '[' : '{';
  const closingBraces = isArray(v) ? ']' : '}';

  return (
    <Box
      pl={`${indent}px`}
      py={1}
      sx={{
        fontSize: 'xs',
        fontFamily: 'monospace',
      }}
    >
      <HStack align="flex-start" onClick={() => setCollapsed(!collapsed)} cursor={isCollapsible(v) ? 'pointer' : 'default'}>
        {isCollapsible(v) && (
          <Box aria-label="Toggle collapse">{collapsed ? <MdArrowRight size={16} /> : <MdArrowDropDown size={16} />}</Box>
        )}
        {comment || !isObject(v) ? (
          <Popover trigger="hover" placement="left" autoFocus={false}>
            <PopoverTrigger>
              <Text as="span" color={keyColor} whiteSpace={'nowrap'}>
                "
                <Text as="span" sx={{ textDecorationColor: '#ccc' }}>
                  {k}
                </Text>
                ": {isCollapsible(v) ? (collapsed ? emptyBraces : opeaningBraces) : ''}
              </Text>
            </PopoverTrigger>
            <Portal>
              <PopoverContent>
                <PopoverArrow />
                <PopoverBody>
                  <Box as="pre" fontSize="xs">
                    <Box as="strong" color="gray.500">
                      "{k}" :
                    </Box>{' '}
                    <Box as="span" color="orange.500" whiteSpace="normal">
                      {isBoolean(v) ? (v ? 'true' : 'false') : JSON.stringify(v)}
                    </Box>
                  </Box>

                  {comment && (
                    <Box padding={2} marginTop={2} borderRadius={theme.radii.md} backgroundColor="#e5f6ea">
                      <HStack
                        as="pre"
                        fontSize="xs"
                        whiteSpace={'pre-line'}
                        display="inline-flex"
                        color="green.500"
                        align="flex-start"
                        width="100%"
                      >
                        <Box color="#4c874e" fontWeight="semibold">
                          {t('formBuilder.infoTooltips.label.title')} :{' '}
                        </Box>
                        <Box as="span" whiteSpace="normal" color="inherit">
                          {comment.label?.[i18n.language]}
                        </Box>
                      </HStack>
                      {comment?.text?.[i18n.language] && (
                        <Stack
                          as="pre"
                          fontSize="xs"
                          whiteSpace={'pre-line'}
                          display="inline-flex"
                          color="green.500"
                          align="flex-start"
                          width="100%"
                          spacing={0}
                        >
                          <Box color="#4c874e" flexShrink={0} fontWeight="semibold">
                            {t('formBuilder.infoTooltips.text.title')} :
                          </Box>
                          <Box
                            as="span"
                            whiteSpace="normal"
                            color="inherit"
                            dangerouslySetInnerHTML={{ __html: comment?.text?.[i18n.language] ?? '' }}
                          />
                        </Stack>
                      )}
                    </Box>
                  )}
                </PopoverBody>
              </PopoverContent>
            </Portal>
          </Popover>
        ) : (
          <Text color={keyColor} whiteSpace={'nowrap'}>
            "{k}": {isCollapsible(v) ? (collapsed ? emptyBraces : opeaningBraces) : ''}
          </Text>
        )}

        {!isCollapsible(v) && (
          <Text color={getTypeColor(v)} isTruncated maxW="120px">
            {isString(v) ? `"${v}"` : String(v)}
          </Text>
        )}
      </HStack>
      {isCollapsible(v) && (
        <Box paddingLeft="8px">
          <Box borderLeft="1px solid" borderLeftColor="#d9dfe2" paddingLeft={'8px'}>
            <Collapse in={!collapsed} animateOpacity>
              <JsonViewer data={v as JsonObject} level={level + 1} comments={comments} path={keyPath} />
            </Collapse>
          </Box>
          {!collapsed && (
            <Text as="span" color={keyColor}>
              {closingBraces}
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
};

const JsonViewer = <T extends K>({ data, comments = {}, level = 0, path = '' }: JsonViewerProps<T>) => {
  return (
    <VStack align="start" spacing={0}>
      {Object.entries(data).map(([k, v]) => (
        <JsonNode key={`${path}.${k}`} k={k} v={v} level={level} path={path} comments={comments} />
      ))}
    </VStack>
  );
};

export default JsonViewer;
